import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Edit2, Trash2, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { teamsApi, type Team, type CreateTeamData, type UpdateTeamData } from '@/services/teamsApi'
import { usersApi, type User } from '@/services/usersApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { useAuthStore } from '@/store/authStore'

const teamSchema = z.object({
  name: z.string().min(1, 'El nombre del equipo es requerido'),
  description: z.string().optional(),
  userIds: z.array(z.string()).optional(),
  isDefault: z.boolean().optional(),
})

type TeamFormData = z.infer<typeof teamSchema>

export default function TeamsManager() {
  const tenantId = useAuthStore((s) => s.tenantId)
  const [teams, setTeams] = useState<Team[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)
  const [deletingTeam, setDeletingTeam] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema as any),
    defaultValues: {
      name: '',
      description: '',
      userIds: [],
      isDefault: false,
    },
  })

  const selectedUserIds = watch('userIds') || []

  useEffect(() => {
    loadData()
  }, [tenantId])

  const loadData = async () => {
    try {
      setLoading(true)
      const [teamsData, usersData] = await Promise.all([
        teamsApi.getTeams(tenantId || undefined),
        usersApi.getUsers(tenantId || undefined),
      ])
      setTeams(teamsData)
      setUsers(usersData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingTeam(null)
    reset({ name: '', description: '', userIds: [] })
    setIsModalOpen(true)
  }

  const openEditModal = (team: Team) => {
    setEditingTeam(team)
    reset({
      name: team.name,
      description: team.description || '',
      userIds: team.users?.map((u) => u.id) || [],
      isDefault: team.isDefault || false,
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingTeam(null)
    reset({ name: '', description: '', userIds: [], isDefault: false })
  }

  const onSubmit = async (data: TeamFormData) => {
    try {
      if (editingTeam) {
        const updateData: UpdateTeamData = {
          name: data.name,
          description: data.description || undefined,
          userIds: data.userIds || [],
          isDefault: data.isDefault || false,
        }
        await teamsApi.updateTeam(editingTeam.id, updateData)
        toast.success('Equipo actualizado exitosamente')
      } else {
        const createData: CreateTeamData = {
          name: data.name,
          description: data.description || undefined,
          userIds: data.userIds || [],
          isDefault: data.isDefault || false,
        }
        await teamsApi.createTeam(createData)
        toast.success('Equipo creado exitosamente')
      }
      closeModal()
      loadData()
    } catch (error: any) {
      console.error('Error saving team:', error)
      toast.error(error?.response?.data?.message || 'Error al guardar el equipo')
    }
  }

  const handleDelete = async (teamId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este equipo?')) {
      return
    }

    try {
      setDeletingTeam(teamId)
      await teamsApi.deleteTeam(teamId)
      toast.success('Equipo eliminado exitosamente')
      loadData()
    } catch (error: any) {
      console.error('Error deleting team:', error)
      toast.error(error?.response?.data?.message || 'Error al eliminar el equipo')
    } finally {
      setDeletingTeam(null)
    }
  }

  const userOptions: MultiSelectOption[] = users
    .filter((user) => !tenantId || !user.tenantId || user.tenantId === tenantId)
    .map((user) => ({
      id: user.id,
      label: user.name || user.email,
      email: user.email,
    }))

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-slate-500 font-medium">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#075E54] border-t-transparent" />
          <span>Cargando equipos...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Equipos de Atención</h2>
            <span className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
              {teams.length} equipo(s)
            </span>
          </div>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            Agrupa a tus asesores y define el equipo por defecto para la recepción y derivación de conversaciones
          </p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl shadow-sm self-start sm:self-auto font-semibold px-4 py-2"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Crear Equipo
        </Button>
      </div>

      {/* Teams Grid */}
      {teams.length === 0 ? (
        <div className="rounded-2xl border border-slate-200/90 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
            <Users className="h-7 w-7" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">No hay equipos creados</h3>
          <p className="mt-1.5 text-xs sm:text-sm text-gray-500 max-w-sm mx-auto">
            Crea tu primer equipo para organizar a tus asesores y asignar conversaciones automáticamente.
          </p>
          <Button
            onClick={openCreateModal}
            className="mt-5 bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl font-semibold shadow-sm"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Crear Primer Equipo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <div
              key={team.id}
              className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm transition-all hover:shadow-md flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-[#075E54]/10 text-[#075E54] flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {team.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-gray-900 truncate">{team.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {team.users?.length || 0} miembro(s)
                      </p>
                    </div>
                  </div>
                  {team.isDefault ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 border border-emerald-200/80 px-2.5 py-0.5 text-[11px] font-bold text-[#075E54] flex-shrink-0">
                      Por Defecto
                    </span>
                  ) : null}
                </div>

                {team.description ? (
                  <p className="mt-3 text-xs sm:text-sm text-gray-600 line-clamp-2">{team.description}</p>
                ) : (
                  <p className="mt-3 text-xs text-gray-400 italic">Sin descripción asignada</p>
                )}

                {/* Integrantes */}
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                    <span className="font-medium text-slate-700">Miembros asignados</span>
                    {team._count && (
                      <span className="text-[11px] text-slate-400 font-medium">
                        {team._count.conversations} conv.
                      </span>
                    )}
                  </div>
                  {team.users && team.users.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {team.users.slice(0, 4).map((u) => (
                        <span
                          key={u.id}
                          className="inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                        >
                          {u.name || u.email.split('@')[0]}
                        </span>
                      ))}
                      {team.users.length > 4 && (
                        <span className="inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 text-xs text-slate-500 font-medium">
                          +{team.users.length - 4} más
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">Sin miembros asignados</p>
                  )}
                </div>
              </div>

              {/* Card actions */}
              <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(team)}
                  className="rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-50 text-xs font-medium"
                >
                  <Edit2 className="mr-1.5 h-3.5 w-3.5 text-slate-500" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(team.id)}
                  disabled={deletingTeam === team.id}
                  className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 text-xs font-medium border-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingTeam ? 'Editar Equipo' : 'Crear Equipo'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Nombre del Equipo *
            </label>
            <Input
              id="name"
              {...register('name')}
              className="mt-1"
              placeholder="Ej: Ventas, Soporte"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Descripción
            </label>
            <textarea
              id="description"
              {...register('description')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#075E54] focus:outline-none focus:ring-1 focus:ring-[#075E54]"
              rows={3}
              placeholder="Descripción del equipo..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Miembros del Equipo
            </label>
            <MultiSelect
              options={userOptions}
              selected={selectedUserIds}
              onChange={(ids) => setValue('userIds', ids)}
              placeholder="Seleccionar miembros..."
              searchPlaceholder="Buscar usuarios..."
              className="mt-1"
            />
            {selectedUserIds.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                {selectedUserIds.length} miembro(s) seleccionado(s)
              </p>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                {...register('isDefault')}
                className="h-4 w-4 rounded border-gray-300 text-[#075E54] focus:ring-[#075E54]"
              />
              <label htmlFor="isDefault" className="text-sm font-medium text-gray-700">
                Equipo por defecto
              </label>
            </div>
            <p className="ml-6 text-xs text-gray-500">
              Las conversaciones nuevas se asignarán automáticamente a este equipo
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : editingTeam ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

